import {t} from "@lingui/core/macro";
import {lazy} from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {isTruthy} from "@khanacademy/wonder-stuff-core";

import {Layout, ToolCategories, type Tool} from "../types.ts";

import pencilIcon from "@phosphor-icons/core/regular/pencil.svg";

const CoeditingDocumentTool = lazy(
    () => import("../renderers/coediting-document-tool.tsx"),
);

export const lessonPlan: Tool = {
    urlSlug: "lesson-plan",
    configName: "teacher-tool-create-lesson-plan",
    title: () => t`Lesson plan`,
    description: () =>
        t`Create structured, detailed lesson plans tailored to your curriculum and students' needs`,
    categories: [ToolCategories.Plan, ToolCategories.Create],
    icon: pencilIcon,
    color: color.fadedPurple24,
    layout: Layout.Coeditor,
    extensionOrder: 3,
    activitySlug: "activity-create-lesson-plan",
    reportingLabelId: "create-lesson-plan",
    exportTargets: [
        "print",
        "download-pdf",
        "download-docx",
        "google-drive-docx",
        "microsoft-onedrive-docx",
        "download-pptx",
        "google-drive-pptx",
        "microsoft-onedrive-pptx",
    ],
    requestedInputs: [
        {
            description: () => t`What are you planning to teach?`,
            inputKind: "CONTENT_SEARCH",
            label: () => t`Describe the topic`,
            required: true,
            variableName: "contentDescriptor",
        },
        {
            description: () => t`What format would you prefer?`,
            inputKind: "GRID_SELECT",
            label: () => t`Format`,
            required: true,
            variableName: "lessonPlanFormat",
            selectOptions: [
                {
                    label: () => t`5-Part`,
                    value: "5p",
                    description: () =>
                        t`Objectives, Introduction, Instruction, Practice, and Assessment`,
                    tooltip: () =>
                        t`A structured approach with five stages: Introduction, Instruction, Guided Practice, Independent Practice, and Closure`,
                },
                {
                    label: () => t`4-Part`,
                    value: "4p",
                    description: () =>
                        t`Warm Up, Classroom Activities, Lesson Synthesis, Cool Down`,
                    tooltip: () =>
                        t`A structured approach with four stages: Warm Up, Classroom Activities, Lesson Synthesis, and Cool Down`,
                },
                {
                    label: () => t`5E`,
                    value: "5e",
                    description: () =>
                        t`Engage, Explore, Explain, Elaborate, and Evaluate`,
                    tooltip: () =>
                        "A student-centered model with five phases: Engage, Explore, Explain, Elaborate, and Evaluate",
                },
                {
                    label: () => t`Inquiry-Based`,
                    value: "inquiry",
                    description: () =>
                        t`Student-driven Questioning, Investigation, and Discovery`,
                    tooltip: () =>
                        "A learning process driven by questioning, exploring, and investigating",
                },
                {
                    label: () => t`UDL`,
                    value: "udl",
                    description: () =>
                        t`Universal Design for Learning. Accomodate diverse learners`,
                    tooltip: () =>
                        "A flexible framework that accomodates all learners by offering multiple means of engagement, representation, and expression",
                },
            ],
        },
        {
            label: () => t`Level of detail`,
            description: () => t`What level of detail would you like?`,
            // This field is hidden until `lessonPlanFormat` is set:
            show: (values) => !!values.lessonPlanFormat,
            inputKind: "GRID_SELECT",
            variableName: "lessonPlanLevelOfDetail",
            selectOptions: (v) =>
                [
                    ["5p", "4p"].includes(v.lessonPlanFormat)
                        ? {
                              label: () => t`High Detail`,
                              value: "high-detail",
                          }
                        : null,
                    {
                        label: () => t`Outline`,
                        value: "outline",
                    },
                ].filter(isTruthy),
        },
    ],
    component: ({providedInputs, configName, debugMode}) => {
        return (
            <CoeditingDocumentTool
                configName={configName}
                providedInputs={providedInputs}
                debugMode={debugMode}
            />
        );
    },
};
